<template>
  <form @submit.prevent="submitForm">
    <!-- Title -->
    <div class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Название *</label>
      <input
        v-model="form.title"
        type="text"
        required
        class="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        :placeholder="titlePlaceholder"
      />
    </div>

    <!-- Content -->
    <div class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Содержание *</label>
      <QuillEditor
        :content="form.content"
        @update:content="(value) => form.content = value"
        content-type="html"
        theme="snow"
        class="bg-white"
        style="border-radius: 0.5rem; overflow: hidden; min-height: 200px;"
      />
    </div>

    <!-- Image (for news, science, contest) -->
    <div v-if="$props.contentType !== 'law'" class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Изображение (Preview)</label>
      <input
        type="file"
        @change="handleImageUpload"
        accept="image/*"
        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
      <div v-if="imagePreview" class="mt-3">
        <img :src="imagePreview" alt="Preview" class="w-32 h-32 object-cover rounded" />
      </div>
    </div>

    <!-- File (for law) -->
    <div v-if="$props.contentType === 'law'" class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Прикрепить файл (PDF, DOCX и т.д.)</label>
      <input
        type="file"
        @change="handleFileUpload"
        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
      <p v-if="fileName" class="text-sm text-gray-600 mt-2">📎 Файл: {{ fileName }}</p>
    </div>

    <!-- UAV Catalog Fields -->
    <div v-if="$props.contentType === 'uav_catalog'" class="mb-6 space-y-4">
      <div>
        <label class="block text-gray-700 text-sm font-bold mb-2">Производитель</label>
        <input
          v-model="form.uav_catalog.manufacturer"
          type="text"
          class="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Введите производителя"
        />
      </div>
      <div>
        <label class="block text-gray-700 text-sm font-bold mb-2">Стоимость</label>
        <input
          v-model="form.uav_catalog.cost"
          type="number"
          step="0.01"
          min="0"
          class="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Введите стоимость"
        />
      </div>
      <div>
        <label class="block text-gray-700 text-sm font-bold mb-2">Назначение</label>
        <input
          v-model="form.uav_catalog.purpose"
          type="text"
          class="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Введите назначение"
        />
      </div>
      <div>
        <label class="block text-gray-700 text-sm font-bold mb-2">Поставщик</label>
        <input
          v-model="form.uav_catalog.supplier"
          type="text"
          class="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Введите поставщика"
        />
      </div>
    </div>

    <!-- Category -->
    <div v-if="$props.contentType !== 'law'" class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Категория *</label>
      <select
        v-model="form.category"
        required
        class="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        <option value="">Выберите категорию</option>
        <option v-for="cat in categories" :key="cat.id" :value="cat.id">
          {{ cat.name }}
        </option>
      </select>
    </div>

    <!-- Info Message -->
    <div class="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <p class="text-blue-800 text-sm">
        ✓ Ваше предложение будет рассмотрено модератором в течение 3 рабочих дней.
        <span v-if="$props.contentType === 'law'">После одобрения закон появится на платформе.</span>
        <span v-else>После одобрения материал появится на платформе.</span>
      </p>
    </div>

    <!-- Buttons -->
    <div class="flex gap-4">
      <button
        type="submit"
        :disabled="loading"
        class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition font-bold text-sm uppercase"
      >
        <span v-if="loading">Отправка...</span>
        <span v-else-if="$props.contentType === 'law'">Предложить закон</span>
        <span v-else>Предложить материал</span>
      </button>
      <button
        type="button"
        @click="$emit('close')"
        class="px-6 py-3 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition font-bold text-sm uppercase"
      >
        Отмена
      </button>
    </div>
  </form>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import { API_CONTENT_SUGGEST, API_CATEGORIES_CLIENT, HEADERS_TOKEN_JSON } from '@/const/uri';

export default {
  name: 'ContentSuggestForm',
  components: {
    QuillEditor,
  },
  props: {
    contentType: {
      type: String,
      default: 'news',
    },
  },
  emits: ['submit', 'close'],
  setup(props, { emit }) {
    const form = ref({
      title: '',
      content: '',
      category: '',
      content_type: props.contentType || 'news',
      uav_catalog: {
        manufacturer: '',
        cost: '',
        purpose: '',
        supplier: '',
      },
    });

    const categories = ref([]);
    const loading = ref(false);
    const imageFile = ref(null);
    const imagePreview = ref(null);
    const fileFile = ref(null);
    const fileName = ref(null);

    const titlePlaceholder = computed(() => {
      const placeholders = {
        news: 'Введите название новости',
        science: 'Введите название статьи',
        contest: 'Введите название соревнования',
        uav_catalog: 'Введите название продукта',
        law: 'Введите название закона',
      };
      return placeholders[props.contentType] || placeholders.news;
    });

    const fetchCategories = async () => {
      try {
        const response = await axios.get(API_CATEGORIES_CLIENT.base, {
          params: {
            content_type: props.contentType || 'news',
          },
        });
        if (response.data.code === 0) {
          categories.value = response.data.data;
        }
      } catch (error) {
        console.error('Ошибка загрузки категорий:', error);
      }
    };

    const handleImageUpload = (event) => {
      const file = event.target.files[0];
      if (file) {
        imageFile.value = file;
        imagePreview.value = URL.createObjectURL(file);
      }
    };

    const handleFileUpload = (event) => {
      const file = event.target.files[0];
      if (file) {
        fileFile.value = file;
        fileName.value = file.name;
      }
    };

    const submitForm = async () => {
      const isCategoryRequired = props.contentType !== 'law';
      if (!form.value.title || !form.value.content || (isCategoryRequired && !form.value.category)) {
        alert('Пожалуйста, заполните все обязательные поля');
        return;
      }

      if (props.contentType === 'law' && !fileFile.value) {
        alert('Пожалуйста, прикрепите файл для закона');
        return;
      }

      loading.value = true;
      try {
        const formData = new FormData();
        formData.append('title', form.value.title);
        formData.append('content', form.value.content);
        formData.append('content_type', form.value.content_type);

        if (props.contentType === 'uav_catalog') {
          formData.append('uav_catalog', JSON.stringify(form.value.uav_catalog));
        }

        if (props.contentType !== 'law') {
          formData.append('category', form.value.category);
          if (imageFile.value) {
            formData.append('image', imageFile.value);
          }
        } else {
          if (fileFile.value) {
            formData.append('file_attachment', fileFile.value);
          }
        }

        const response = await axios.post(
          API_CONTENT_SUGGEST.create,
          formData,
          {
            headers: {
              ...HEADERS_TOKEN_JSON(),
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        if (response.data.code === 0 || response.status === 201) {
          emit('submit', response.data.data);
          alert('После одобрения модератором публикация появится на платформе в течение 3 рабочих дней.');
        }
      } catch (error) {
        console.error('Ошибка при отправке формы:', error);
        alert('Ошибка при отправке формы. Пожалуйста, попробуйте снова.');
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      fetchCategories();
    });

    return {
      form,
      categories,
      loading,
      imagePreview,
      fileName,
      titlePlaceholder,
      handleImageUpload,
      handleFileUpload,
      submitForm,
    };
  },
};
</script>

<style scoped>
:deep(.ql-container) {
  font-size: 1rem;
}

:deep(.ql-editor) {
  min-height: 200px;
  padding: 0.75rem;
}
</style>
